import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Card, Alert, Tab, Tabs } from 'react-bootstrap';
import {
  useGetSecurityGroupByIdQuery,
  useUpdateSecurityGroupMutation,
  useGetUsersBySecurityGroupQuery,
  useGetReportsBySecurityGroupQuery
} from '../../services/portal';
import GroupUsers from './GroupUsers';
import GroupReports from './GroupReports';

/**
 * Page component for viewing and editing a security group's details.
 * Fetches group data, displays metadata and counts, and allows updating the group's
 * name, description, and active status. Also links to Users and Reports tabs.
 * @returns {JSX.Element} Edit Security Group page
 */
const EditSecurityGroup = () => {
  const { securityGroupId } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    groupName: '',
    groupDescription: '',
    isActive: true
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  const { data: securityGroup, isLoading, refetch } = useGetSecurityGroupByIdQuery(securityGroupId);
  const { data: groupUsers } = useGetUsersBySecurityGroupQuery(securityGroupId);
  const { data: groupReports } = useGetReportsBySecurityGroupQuery(securityGroupId);
  const [updateSecurityGroup] = useUpdateSecurityGroupMutation();

  useEffect(() => {
    if (securityGroup) {
      setFormData({
        groupName: securityGroup.GroupName || '',
        groupDescription: securityGroup.GroupDescription || '',
        isActive: securityGroup.IsActive !== false
      });
    }
  }, [securityGroup]);

  /**
   * Handles any form control change:
   * - Updates formData (supports text, textarea, checkbox)
   * - Clears the field-specific error if present
   * @param {React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLInputElement>} e - Input change event
   * @returns {void}
   */
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  /**
   * Validates the current form data and returns an error map.
   * @returns {Record<string, string>} Object where keys are field names and values are error messages
   */
  const validateForm = () => {
    const newErrors = {};

    if (!formData.groupName.trim()) {
      newErrors.groupName = 'Group name is required';
    } else if (formData.groupName.length > 100) {
      newErrors.groupName = 'Group name must be less than 100 characters';
    }

    if (formData.groupDescription && formData.groupDescription.length > 500) {
      newErrors.groupDescription = 'Description must be less than 500 characters';
    }

    return newErrors;
  };

  /**
   * Submits the form. Validates inputs and shows field errors if any
   * Calls API to update security group.
   * Refetches group data and shows success on success; shows submit error on failure.
   * @param {React.FormEvent<HTMLFormElement>} e - Form submit event
   * @returns {Promise<void>}
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      await updateSecurityGroup({
        securityGroupId: parseInt(securityGroupId),
        group_name: formData.groupName,
        group_description: formData.groupDescription || null,
        is_active: formData.isActive
      }).unwrap();

      refetch();
      setErrors({ success: 'Security group updated successfully!' });
    } catch (error) {
      console.error('Failed to update security group:', error);
      setErrors({ submit: 'Failed to update security group. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Navigates back to the Security Groups list.
   * @returns {void}
   */
  const handleCancel = () => navigate('/security-groups');

  if (isLoading) {
    return (
      <div className="page">
        <div className="page-inner">
          <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!securityGroup) {
    return (
      <div className="page">
        <div className="page-inner">
          <Alert variant="danger">
            Security group not found.
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-inner">
        <header className="page-title-bar">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="/security-groups">
                  <i className="breadcrumb-icon fa fa-angle-left mr-2"></i>
                  Security Groups
                </a>
              </li>
            </ol>
          </nav>
          <div className="d-flex justify-content-between">
            <h1 className="page-title">Edit Security Group</h1>
          </div>
        </header>

        <div className="page-section">
          <Tabs
            activeKey={activeTab}
            onSelect={setActiveTab}
            className="mb-3"
          >
            <Tab eventKey="details" title="Group Details">
              <Card>
                    <Card.Header>
                      <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Group Information</h5>
                        <div className="d-flex text-center">
                          <div style={{ marginRight: '24px' }}>
                            <div className="h6 mb-0">{groupUsers?.users?.length || 0}</div>
                            <div className="text-muted small">Users</div>
                          </div>
                          <div>
                            <div className="h6 mb-0">{groupReports?.reports?.length || 0}</div>
                            <div className="text-muted small">Reports</div>
                          </div>
                        </div>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <div className="col-md-6 mb-3">
                        <p className="text-muted small">
                          Modify group settings, assign users to this group, and manage report permissions.
                        </p>
                      </div>

                      {errors.submit && (
                        <Alert variant="danger" className="mb-3">
                          {errors.submit}
                        </Alert>
                      )}

                      {errors.success && (
                        <Alert variant="success" className="mb-3">
                          {errors.success}
                        </Alert>
                      )}

                      <Form onSubmit={handleSubmit}>
                        <Form.Group className="form-group col-md-6 mb-3">
                          <Form.Label>Group Name *</Form.Label>
                          <Form.Control
                            type="text"
                            name="groupName"
                            value={formData.groupName}
                            onChange={handleInputChange}
                            isInvalid={!!errors.groupName}
                            maxLength={100}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.groupName}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="form-group col-md-6 mb-3">
                          <Form.Label>Description</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            name="groupDescription"
                            value={formData.groupDescription}
                            onChange={handleInputChange}
                            isInvalid={!!errors.groupDescription}
                            maxLength={500}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.groupDescription}
                          </Form.Control.Feedback>
                          <Form.Text className="text-muted">
                            {formData.groupDescription.length}/500 characters
                          </Form.Text>
                        </Form.Group>

                        <Form.Group className="form-group col-md-6 mb-3">
                          <Form.Label>Organization</Form.Label>
                          <Form.Control
                            type="text"
                            value={securityGroup.OrganizationName}
                            disabled
                          />
                          <Form.Text className="text-muted">
                            Organization cannot be changed after creation
                          </Form.Text>
                        </Form.Group>

                        <Form.Group className="form-group col-md-6 mb-3">
                          <Form.Check
                            type="checkbox"
                            name="isActive"
                            checked={formData.isActive}
                            onChange={handleInputChange}
                            label="Active"
                          />
                          <Form.Text className="text-muted">
                            Inactive groups will not grant permissions to users
                          </Form.Text>
                          <div className="small text-muted mt-2">
                            <strong>Created:</strong> {securityGroup.CreateDate ? new Date(securityGroup.CreateDate).toLocaleDateString() : 'N/A'}
                          </div>
                        </Form.Group>

                        <hr className="mb-0 mt-0"></hr>
                        <div className="form-actions">
                          <button
                            className="btn btn-primary"
                            type="submit"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? 'Updating...' : 'Update Group'}
                          </button>
                          <button
                            className="btn btn-secondary"
                            style={{ marginLeft: 10 }}
                            type="button"
                            onClick={handleCancel}
                            disabled={isSubmitting}
                          >
                            Back to List
                          </button>
                        </div>
                      </Form>
                    </Card.Body>
                  </Card>
            </Tab>

            <Tab eventKey="users" title={`Users (${groupUsers?.users?.length || 0})`}>
              <GroupUsers securityGroupId={securityGroupId} organizationId={securityGroup.fkOrganizationID} />
            </Tab>

            <Tab eventKey="reports" title={`Reports (${groupReports?.reports?.length || 0})`}>
              <GroupReports securityGroupId={securityGroupId} organizationId={securityGroup.fkOrganizationID} />
            </Tab>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default EditSecurityGroup;
